import { __rest } from '../node_modules/tslib/tslib.es6.js';
import React__default, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { Badge } from '../Components/ui/badge.js';
import { Carousel, CarouselContent, CarouselItem } from '../Components/ui/carousel.js';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../Components/ui/hover-card.js';
import { cn } from '../lib/utils.js';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';

const InlineCitation = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React__default.createElement("span", Object.assign({ className: cn("tw:group tw:inline tw:items-center tw:gap-1", className) }, props)));
};
const InlineCitationText = (_a) => {
    var { className, url, children } = _a, props = __rest(_a, ["className", "url", "children"]);
    if (url) {
        return (React__default.createElement("a", Object.assign({ href: url, target: "_blank", rel: "noopener noreferrer", className: cn("tw:transition-colors tw:group-hover:bg-accent tw:cursor-pointer tw:underline", className) }, props), children));
    }
    return (React__default.createElement("span", Object.assign({ className: cn("tw:transition-colors tw:group-hover:bg-accent", className) }, props), children));
};
const InlineCitationCard = (props) => (React__default.createElement(HoverCard, Object.assign({ closeDelay: 0, openDelay: 0 }, props)));
const InlineCitationCardTrigger = (_a) => {
    var { sources, className } = _a, props = __rest(_a, ["sources", "className"]);
    return (React__default.createElement(HoverCardTrigger, { asChild: true },
        React__default.createElement(Badge, Object.assign({ className: cn("tw:ml-1 tw:rounded-full", className), variant: "secondary" }, props), sources[0] ? (React__default.createElement(React__default.Fragment, null,
            new URL(sources[0]).hostname,
            " ",
            sources.length > 1 && `+${sources.length - 1}`)) : ("unknown"))));
};
const InlineCitationCardBody = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React__default.createElement(HoverCardContent, Object.assign({ className: cn("tw:relative tw:w-80 tw:p-0", className) }, props)));
};
const CarouselApiContext = createContext(undefined);
const useCarouselApi = () => {
    const context = useContext(CarouselApiContext);
    return context;
};
const InlineCitationCarousel = (_a) => {
    var { className, children } = _a, props = __rest(_a, ["className", "children"]);
    const [api, setApi] = useState();
    return (React__default.createElement(CarouselApiContext.Provider, { value: api },
        React__default.createElement(Carousel, Object.assign({ className: cn("tw:w-full", className), setApi: setApi }, props), children)));
};
const InlineCitationCarouselContent = (props) => React__default.createElement(CarouselContent, Object.assign({}, props));
const InlineCitationCarouselItem = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React__default.createElement(CarouselItem, Object.assign({ className: cn("tw:w-full tw:space-y-2 tw:p-4 tw:pl-8", className) }, props)));
};
const InlineCitationCarouselHeader = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React__default.createElement("div", Object.assign({ className: cn("tw:flex tw:items-center tw:justify-between tw:gap-2 tw:rounded-t-md tw:bg-secondary tw:p-2", className) }, props)));
};
const InlineCitationCarouselIndex = (_a) => {
    var { children, className } = _a, props = __rest(_a, ["children", "className"]);
    const api = useCarouselApi();
    const [current, setCurrent] = useState(0);
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!api) {
            return;
        }
        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap() + 1);
        api.on("select", () => {
            setCurrent(api.selectedScrollSnap() + 1);
        });
    }, [api]);
    return (React__default.createElement("div", Object.assign({ className: cn("tw:flex tw:flex-1 tw:items-center tw:justify-end tw:px-3 tw:py-1 tw:text-muted-foreground tw:text-xs", className) }, props), children !== null && children !== void 0 ? children : `${current}/${count}`));
};
const InlineCitationCarouselPrev = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const api = useCarouselApi();
    const handleClick = useCallback(() => {
        if (api) {
            api.scrollPrev();
        }
    }, [api]);
    return (React__default.createElement("button", Object.assign({ "aria-label": "Previous", className: cn("tw:shrink-0", className), onClick: handleClick, type: "button" }, props),
        React__default.createElement(ArrowLeftIcon, { className: "tw:size-4 tw:text-muted-foreground" })));
};
const InlineCitationCarouselNext = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    const api = useCarouselApi();
    const handleClick = useCallback(() => {
        if (api) {
            api.scrollNext();
        }
    }, [api]);
    return (React__default.createElement("button", Object.assign({ "aria-label": "Next", className: cn("tw:shrink-0", className), onClick: handleClick, type: "button" }, props),
        React__default.createElement(ArrowRightIcon, { className: "tw:size-4 tw:text-muted-foreground" })));
};
const InlineCitationSource = (_a) => {
    var { title, url, description, className, children } = _a, props = __rest(_a, ["title", "url", "description", "className", "children"]);
    return (React__default.createElement("div", Object.assign({ className: cn("tw:space-y-1", className) }, props),
        title && (React__default.createElement("h4", { className: "tw:truncate tw:font-medium tw:text-sm tw:leading-tight" }, url ? (React__default.createElement("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "tw:cursor-pointer tw:hover:underline" }, title)) : (title))),
        url && (React__default.createElement("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "tw:truncate tw:break-all tw:text-muted-foreground tw:text-xs tw:cursor-pointer tw:hover:underline tw:block" }, url)),
        description && (React__default.createElement("p", { className: "tw:line-clamp-3 tw:text-muted-foreground tw:text-sm tw:leading-relaxed" }, description)),
        children));
};

export { InlineCitation, InlineCitationCard, InlineCitationCardBody, InlineCitationCardTrigger, InlineCitationCarousel, InlineCitationCarouselContent, InlineCitationCarouselHeader, InlineCitationCarouselIndex, InlineCitationCarouselItem, InlineCitationCarouselNext, InlineCitationCarouselPrev, InlineCitationSource, InlineCitationText };
//# sourceMappingURL=inline-citation.js.map
